/**
 * Вызовы REST Битрикс24 из браузера.
 *
 * Через них идёт только работа с профилем дела: всё остальное делает сервер.
 * Ограничение частоты портала (около двух запросов в секунду) соблюдается
 * и здесь: карточка открывается несколькими обращениями подряд.
 */

import { bx24 } from '../bx24.js';

export class B24CallError extends Error {
  constructor(message: string, readonly code: string, readonly method: string) {
    super(message);
    this.name = 'B24CallError';
  }
}

/** Последовательная очередь: портал не любит всплесков обращений. */
class Queue {
  private chain: Promise<unknown> = Promise.resolve();
  private last = 0;

  constructor(private readonly minIntervalMs: number) {}

  run<T>(task: () => Promise<T>): Promise<T> {
    const result = this.chain.then(async () => {
      const wait = this.minIntervalMs - (Date.now() - this.last);
      if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
      this.last = Date.now();
      return task();
    });
    // Одна ошибка не должна обрывать очередь для остальных вызовов
    this.chain = result.catch(() => undefined);
    return result;
  }
}

const queue = new Queue(1000 / 2);

export function call<T = unknown>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  return queue.run(
    () =>
      new Promise<T>((resolve, reject) => {
        const api = bx24();
        if (!api) {
          reject(new B24CallError('Приложение открыто вне портала Битрикс24', 'NO_PORTAL', method));
          return;
        }

        api.callMethod(method, params, (result) => {
          const error = result.error();
          if (error) {
            reject(
              new B24CallError(
                result.error_description?.() ?? 'Портал Битрикс24 вернул ошибку',
                typeof error === 'string' ? error : 'B24_ERROR',
                method,
              ),
            );
            return;
          }
          resolve(result.data() as T);
        });
      }),
  );
}

/**
 * Постраничное чтение списочных методов.
 * Портал отдаёт по 50 записей и возвращает смещение в поле `next`.
 */
export async function callList<T>(
  method: string,
  params: Record<string, unknown> = {},
  itemsKey = 'items',
): Promise<T[]> {
  const all: T[] = [];
  let start = 0;

  for (;;) {
    const page = (await call<Record<string, unknown> & { next?: number }>(method, { ...params, start }));
    const items = (page?.[itemsKey] ?? page) as T[];
    if (Array.isArray(items)) all.push(...items);

    if (typeof page?.next !== 'number') break;
    start = page.next;
    if (all.length > 10_000) break; // защита от бесконечного цикла
  }

  return all;
}

/** Байты → base64 для передачи файла в REST. */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000; // посимвольная сборка переполняет стек на больших файлах
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
